import { useMutation, useQueryClient } from '@tanstack/react-query';

import { dismissTask, taskQueries } from '../api/task';
import useTaskApiInstance from './useTaskApiInstance';

import type { AxiosError } from 'axios';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { TaskDismissResponse, TaskListResponse } from '../constants';

type UseDismissTaskOptions = UseMutationOptions<TaskDismissResponse, AxiosError, string>;

/**
 * 단일 Task 숨김 처리 훅 (성공/실패 항목 개별 X 버튼).
 * @description
 * - 성공하면 `taskQueries.all()`을 invalidate 해서 list를 최신화.
 */
const useDismissTask = (options?: UseDismissTaskOptions) => {
  const queryClient = useQueryClient();
  const { instance } = useTaskApiInstance();

  return useMutation({
    mutationFn: (taskId: string) => dismissTask(instance, taskId),
    ...options,
    onMutate: async taskId => {
      const queryKey = taskQueries.lists();

      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueriesData<TaskListResponse>({ queryKey }, old => {
        if (!old) return old;

        const nextTasks = old.data.filter(task => task.id !== taskId);
        const removedCount = old.data.length - nextTasks.length;

        return {
          ...old,
          data: nextTasks,
          totalCount: Math.max(0, old.totalCount - removedCount),
        };
      });

      await options?.onMutate?.(taskId);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: taskQueries.all() });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export default useDismissTask;
